import React, { useMemo } from "react";
import { RfiActionToastProvider } from "../../contexts/RfiActionToastContext";
import CustomTable from "../CustomTable";
import RfiTypeBadge from "./RfiTypeBadge";
import RfiTemplateServiceBadges from "./RfiTemplateServiceBadges";
import { MenuDropdownAccordingToStatuses } from "../RFIsComponents/MenuDropdownAccordingToStatuses";
import GridStatusDotCell from "../GridStatusDotCell";
import { mapMyRfiItemToGridRow } from "../../utils/myRfiGridRowMapper";
import { resolveRfiListRowStatus } from "../../utils/rfiRequestActionPermissions";
import { getGridArchiveCommentDisplay } from "../../utils/gridArchiveCommentDisplay";
import RiskSummaryCell from "./RiskSummaryCell";
import { useRiskSummariesByIds } from "../../custom-hooks/queries";
import {
  extractRiskRowIds,
  getRowRiskSummary,
  makeRiskSummarySortComparator,
} from "../../utils/riskSummaryGridUtils";
import {
  COMPACT_GRID_COL_WIDTH_PCT,
  compactGridCycleCellClassName,
  compactGridDateCellClassName,
  compactGridStatusCellClassName,
  getRfiGridCellPaddingClass,
  SIZE_TABLE_COL_MIN,
  SIZE_TABLE_TEXT_COL_MAX,
} from "./rfiGridTableStyles";

/**
 * Admin RFI list grid: Innovation Lab styling + Sponsor column + same column rules as RfiGridTable.
 */
export default function BackendRfiGridTable({
  data,
  sortConfig,
  onSort,
  onRowClick,
  refetch,
  isSearchActive = false,
  emptyMessage = "No VQQs found",
  emptyDescription = "No VQQ requests are available at this time.",
  className = "py-0 sm:py-0",
  rowKey = "questionnaire_id",
}) {
  // 14029 — Admin always sees the Risk Summary column.
  const rowIds = useMemo(() => extractRiskRowIds(data), [data]);
  const { data: summariesByRowId } = useRiskSummariesByIds(rowIds, {
    enabled: rowIds.length > 0,
  });
  const summaryMap = summariesByRowId ?? {};

  const columns = useMemo(
    () => [
      {
        key: "sponsor",
        header: "Sponsor",
        sortable: true,
        width: "10%",
        minWidth: SIZE_TABLE_COL_MIN.org,
        contentMaxWidth: SIZE_TABLE_TEXT_COL_MAX,
        cellClassName: "text-gray-900 font-medium",
        render: (item) => (
          <span className="break-words">{item?.sponsorName || "--"}</span>
        ),
      },
      {
        key: "vendor",
        header: "Vendor",
        sortable: true,
        width: "10%",
        minWidth: SIZE_TABLE_COL_MIN.org,
        contentMaxWidth: SIZE_TABLE_TEXT_COL_MAX,
        cellClassName: "text-gray-900 font-medium",
        render: (item) => {
          const row = mapMyRfiItemToGridRow(item);
          return <span className="break-words">{row.vendor}</span>;
        },
      },
      {
        key: "type",
        header: "Type",
        sortable: false,
        width: "7%",
        minWidth: SIZE_TABLE_COL_MIN.type,
        render: (item) => <RfiTypeBadge item={item} />,
      },
      {
        key: "template",
        header: "Template",
        sortable: true,
        width: "11%",
        minWidth: SIZE_TABLE_COL_MIN.template,
        contentMaxWidth: SIZE_TABLE_TEXT_COL_MAX,
        render: (item) => (
          <RfiTemplateServiceBadges
            services={item?.services ?? item?.Services}
            fallbackTemplateName={item?.name}
          />
        ),
      },
      {
        key: "location",
        header: "Location",
        sortable: true,
        width: "9%",
        minWidth: SIZE_TABLE_COL_MIN.location,
        contentMaxWidth: SIZE_TABLE_TEXT_COL_MAX,
        cellClassName: "text-gray-900",
        render: (item) => {
          const row = mapMyRfiItemToGridRow(item);
          return row.location ? (
            <span className="break-words">{row.location}</span>
          ) : (
            <span className="text-gray-400">--</span>
          );
        },
      },
      {
        key: "risk",
        header: "Risk Summary",
        sortable: true,
        width: "10%",
        cellClassName: "text-gray-800",
        // Same lookup as the sort and the VQQ screen: a VQQ that never asked for
        // risk scoring shows "--", admins included.
        render: (item) => (
          <RiskSummaryCell summary={getRowRiskSummary(item, summaryMap)} />
        ),
      },
      {
        key: "status",
        header: "Status",
        sortable: true,
        width: COMPACT_GRID_COL_WIDTH_PCT,
        minWidth: SIZE_TABLE_COL_MIN.status,
        cellClassName: compactGridStatusCellClassName,
        render: (item) => (
          <GridStatusDotCell status={resolveRfiListRowStatus(item)} />
        ),
      },
      {
        key: "requestDate",
        header: "Request Date",
        sortable: true,
        width: COMPACT_GRID_COL_WIDTH_PCT,
        minWidth: SIZE_TABLE_COL_MIN.date,
        cellClassName: compactGridDateCellClassName,
        headerClassName: "text-center [&>div]:justify-center",
        render: (item) => {
          const row = mapMyRfiItemToGridRow(item);
          return row.requestDateDisplay || (
            <span className="text-gray-400">--</span>
          );
        },
      },
      {
        key: "dueDate",
        header: "Due Date",
        sortable: true,
        width: COMPACT_GRID_COL_WIDTH_PCT,
        minWidth: SIZE_TABLE_COL_MIN.date,
        cellClassName: compactGridDateCellClassName,
        headerClassName: "text-center [&>div]:justify-center",
        render: (item) => {
          const row = mapMyRfiItemToGridRow(item);
          return row.dueDateDisplay || (
            <span className="text-gray-400">--</span>
          );
        },
      },
      {
        key: "releasedDate",
        header: "Release Date",
        sortable: true,
        width: COMPACT_GRID_COL_WIDTH_PCT,
        minWidth: SIZE_TABLE_COL_MIN.date,
        cellClassName: compactGridDateCellClassName,
        headerClassName: "text-center [&>div]:justify-center",
        render: (item) => {
          const row = mapMyRfiItemToGridRow(item);
          return row.initialReleaseDateDisplay || (
            <span className="text-gray-400">--</span>
          );
        },
      },
      {
        key: "completionDate",
        header: "Completion Date",
        sortable: true,
        width: COMPACT_GRID_COL_WIDTH_PCT,
        minWidth: SIZE_TABLE_COL_MIN.date,
        cellClassName: compactGridDateCellClassName,
        headerClassName: "text-center [&>div]:justify-center",
        render: (item) => {
          const row = mapMyRfiItemToGridRow(item);
          return row.completionDateDisplay || (
            <span className="text-gray-400">--</span>
          );
        },
      },
      {
        key: "cycleTime",
        header: "Cycle Time",
        sortable: true,
        width: COMPACT_GRID_COL_WIDTH_PCT,
        minWidth: SIZE_TABLE_COL_MIN.cycle,
        cellClassName: compactGridCycleCellClassName,
        headerClassName: "text-center [&>div]:justify-center",
        render: (item) => {
          const row = mapMyRfiItemToGridRow(item);
          return row.cycleTime || <span className="text-gray-400">--</span>;
        },
      },
      {
        key: "archiveComment",
        header: "Archive Comment",
        sortable: false,
        width: "10%",
        minWidth: SIZE_TABLE_COL_MIN.document,
        contentMaxWidth: SIZE_TABLE_TEXT_COL_MAX,
        cellClassName: "text-gray-500",
        render: (item) => {
          const text = getGridArchiveCommentDisplay(item);
          return text ? (
            <span className="italic text-xs line-clamp-2" title={text}>
              {text}
            </span>
          ) : (
            <span className="text-gray-300">--</span>
          );
        },
      },
      {
        key: "actions",
        header: "Actions",
        sortable: false,
        minWidth: SIZE_TABLE_COL_MIN.actions,
        align: "right",
        cellClassName: "text-right",
        excludeFromRowClassName: true,
        render: (item) => (
          <div onClick={(e) => e.stopPropagation()}>
            <MenuDropdownAccordingToStatuses
              status={resolveRfiListRowStatus(item)}
              type="RFI"
              isMyRFI={true}
              isDocLibrary={false}
              questionnaireId={item?.questionnaire_id}
              sponsorRfiResponseId={item?.sponsorRfiResponseId}
              details={item}
              isLegacyRfi={item?.v1}
              v1_doc_id={item?.mongo_sponsor_rfiresponse_id}
              v1_doc_name={item?.v1_document_name}
              refetch={refetch}
              isRfiBackendAdmin={true}
            />
          </div>
        ),
      },
    ],
    [refetch, summaryMap]
  );

  // 14029 — Risk Summary column re-sorts client-side; `--` to bottom in both directions.
  const displayData = useMemo(() => {
    const activeKey = Object.keys(sortConfig || {})[0];
    if (activeKey !== "risk") return data;
    const direction = sortConfig.risk?.direction || "desc";
    return [...(data || [])].sort(
      makeRiskSummarySortComparator(summaryMap, direction)
    );
  }, [data, sortConfig, summaryMap]);

  return (
    <RfiActionToastProvider>
      <div className="w-full min-w-0 max-w-full">
        <CustomTable
          columns={columns}
          data={displayData}
          sortConfig={sortConfig}
          onSort={onSort}
          fixedColumnWidths
          emptyMessage={emptyMessage}
          emptyDescription={emptyDescription}
          isSearchActive={isSearchActive}
          edgeToEdge
          className={className}
          rowKey={rowKey}
          onRowClick={onRowClick}
          getHeaderCellPaddingClass={(colIndex, totalCols) =>
            getRfiGridCellPaddingClass(colIndex, totalCols, true)
          }
          getBodyCellPaddingClass={(colIndex, totalCols) =>
            getRfiGridCellPaddingClass(colIndex, totalCols, false)
          }
          scrollWrapperClassName="bg-white"
          tableDivideClassName="divide-gray-200"
          theadClassName="bg-gray-50 [&_th]:align-middle [&_th]:min-h-[3rem]"
          tbodyClassName="divide-y divide-gray-200 bg-white"
          useDividedBodyRows
          getRowClassName={(item) =>
            resolveRfiListRowStatus(item) === "Archived" ? "opacity-60" : ""
          }
        />
      </div>
    </RfiActionToastProvider>
  );
}
